/**
 * Datum temperature / Q-value determination workbench (ASTM C1074-19 Appendix X1).
 *
 * The lab enters average mortar-cube strengths at each test age for three or
 * more water-bath temperatures. The backend fits the strength-age curve for
 * each bath, then derives the datum temperature (Nurse-Saul) and the Q-value
 * (Arrhenius). Results, quality warnings and the three plots the standard
 * describes are shown here, and each result can be saved straight into a
 * Nurse-Saul or Arrhenius maturity input group.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Calculate as CalculateIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
  RestartAlt as ResetIcon,
  Save as SaveIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import maturityService, {
  type DatumQAnalyzeRequest,
  type DatumQAnalyzeResponse,
  type DatumQExampleResponse,
} from '../../services/maturity';
import { strengthUnitFromFormat } from '../../utils/strengthUnit';
import { labelWithUnit } from '../../utils/maturityDisplay';
import { apiErrorText } from '../../utils/apiError';
import {
  type BathForm,
  MIN_BATHS,
  bathsFromExample,
  buildRequest,
  cToFNumber,
  emptyBaths,
  fmt,
  hyperbolicCurve,
  linePoints,
  newBath,
  newRow,
} from './datumQMath';

export interface DatumQNurseSaulPrefill {
  datum_temp: string;
  group_description: string;
}

export interface DatumQArrheniusPrefill {
  activation_energy: string;
  specified_temp: string;
  group_description: string;
}

interface Props {
  canManage: boolean;
  onUseForNurseSaul: (values: DatumQNurseSaulPrefill) => void;
  onUseForArrhenius: (values: DatumQArrheniusPrefill) => void;
  /** The company's strength unit (StrengthFormat): the default choice. No
   *  unit is chosen while it is unknown, never a guessed one. */
  strengthUnit?: string | null;
}

const COLORS = ['#1976d2', '#e65100', '#2e7d32', '#6a1b9a', '#c62828', '#00838f'];
// The three company strength units (StrengthFormat 01 / 02 / 03), spelled as
// everywhere else in the app.
const UNITS = ['01', '02', '03'].map((format) => strengthUnitFromFormat(format));
/** A unit in the app's spelling ("psi" -> "Psi"); other text as given. */
const knownUnit = (unit?: string | null): string | null => {
  const u = (unit ?? '').trim();
  if (!u) return null;
  return UNITS.find((x) => x.toLowerCase() === u.toLowerCase()) ?? u;
};
const DEFAULT_SPECIFIED_TEMP_C = '20'; // X2.3.2: traditionally 20 °C

const baseChart: Partial<Highcharts.Options> = {
  credits: { enabled: false },
  chart: { height: 340 },
  legend: { enabled: true },
};

const DatumQWorkbench: React.FC<Props> = ({ canManage, onUseForNurseSaul, onUseForArrhenius, strengthUnit }) => {
  const { t } = useTranslation();
  const [baths, setBaths] = useState<BathForm[]>(() => emptyBaths());
  // The user's choice, else the company's unit once known.
  const [chosenUnit, setUnit] = useState<string | null>(null);
  const unit = chosenUnit ?? knownUnit(strengthUnit) ?? '';
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [apiError, setApiError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DatumQAnalyzeResponse | null>(null);
  // The exact request that produced `result`, so results, charts and Save
  // buttons always describe the same data even while the grid is edited.
  const [analyzed, setAnalyzed] = useState<DatumQAnalyzeRequest | null>(null);
  const [example, setExample] = useState<DatumQExampleResponse | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // ---- grid editing -------------------------------------------------------
  // Any edit invalidates the current result and the "this is the ASTM example" state.
  const edit = (update: (prev: BathForm[]) => BathForm[]) => {
    setResult(null);
    setAnalyzed(null);
    setExample(null);
    setBaths(update);
  };

  const updateBath = (bathId: string, patch: Partial<BathForm>) =>
    edit((prev) => prev.map((b) => (b.id === bathId ? { ...b, ...patch } : b)));

  const updateRow = (bathId: string, rowId: string, field: 'age' | 'strength', value: string) =>
    edit((prev) =>
      prev.map((b) =>
        b.id !== bathId
          ? b
          : { ...b, rows: b.rows.map((r) => (r.id === rowId ? { ...r, [field]: value } : r)) },
      ),
    );

  const addRow = (bathId: string) =>
    edit((prev) => prev.map((b) => (b.id === bathId ? { ...b, rows: [...b.rows, newRow()] } : b)));

  const removeRow = (bathId: string, rowId: string) =>
    edit((prev) =>
      prev.map((b) => (b.id === bathId ? { ...b, rows: b.rows.filter((r) => r.id !== rowId) } : b)),
    );

  const addBath = () => edit((prev) => [...prev, newBath()]);
  const removeBath = (bathId: string) => edit((prev) => prev.filter((b) => b.id !== bathId));

  const reset = () => {
    edit(() => emptyBaths());
    setFormErrors([]);
    setApiError('');
  };

  // ---- actions ------------------------------------------------------------
  const loadExample = useCallback(async () => {
    setApiError('');
    setFormErrors([]);
    try {
      const ex = await maturityService.getDatumQExample();
      edit(() => bathsFromExample(ex.baths));
      setUnit(knownUnit(ex.strength_unit));
      setExample(ex);
    } catch (e: any) {
      setApiError(apiErrorText(e, t('maturityTarget.datumQ.loadExampleError', 'Could not load the ASTM example')));
    }
  }, [t]);

  const calculate = useCallback(async () => {
    setApiError('');
    setResult(null);
    setAnalyzed(null);
    const built = buildRequest(baths, unit);
    const errors = unit
      ? built.errors
      : [t('maturityTarget.datumQ.chooseUnit', 'Choose the strength unit of the test results.'), ...built.errors];
    const request = unit ? built.request : null;
    setFormErrors(errors);
    if (!request) return;
    setLoading(true);
    try {
      const res = await maturityService.analyzeDatumQ(request);
      setResult(res);
      setAnalyzed(request);
    } catch (e: any) {
      setApiError(apiErrorText(e, t('maturityTarget.datumQ.analyzeError', 'The analysis could not be completed')));
    } finally {
      setLoading(false);
    }
  }, [baths, unit, t]);

  // `example` is cleared by any edit, so its presence means the grid still holds
  // the untouched ASTM data and the "standard reports" comparison is valid.
  const isExampleLoaded = example !== null;

  // Colour of a bath is fixed by the order the user entered it (grid card order),
  // so the same colour identifies it in the results table and charts even though
  // the backend returns baths sorted by temperature.
  const colorForTemp = (tempC: number, fallbackIndex: number): string => {
    const idx = analyzed ? analyzed.baths.findIndex((b) => b.bath_temp_c === tempC) : -1;
    return COLORS[(idx >= 0 ? idx : fallbackIndex) % COLORS.length];
  };

  // ---- charts -------------------------------------------------------------
  const strengthChart = useMemo<Highcharts.Options | null>(() => {
    if (!result || !analyzed) return null;
    const series: Highcharts.SeriesOptionsType[] = [];
    result.baths.forEach((b, i) => {
      const color = colorForTemp(b.bath_temp_c, i);
      const input = analyzed.baths.find((x) => x.bath_temp_c === b.bath_temp_c);
      const measured: number[][] = (input?.points ?? []).map((p) => [p.age_days, p.strength]);
      const maxAge = measured.reduce((m, [a]) => Math.max(m, a), 0) * 1.05;
      series.push({
        type: 'scatter',
        name: `${b.bath_temp_c} °C ${t('maturityTarget.datumQ.measured', 'measured')}`,
        data: measured,
        color,
        marker: { radius: 5, symbol: 'circle' },
        zIndex: 3,
      });
      series.push({
        type: 'line',
        name: `${b.bath_temp_c} °C ${t('maturityTarget.datumQ.fitted', 'fitted curve')}`,
        data: hyperbolicCurve(b.su, b.k, b.t0, maxAge),
        color,
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 2,
      });
    });
    return {
      ...baseChart,
      title: { text: t('maturityTarget.datumQ.chartStrengthTitle', 'Step 1: Strength vs age for each bath') },
      subtitle: { text: t('maturityTarget.datumQ.chartStrengthSub', 'Eq X1.1 hyperbolic fit. The slope near the start is the rate constant k.') },
      xAxis: { title: { text: t('maturityTarget.datumQ.ageDays', 'Age (days)') }, min: 0 },
      yAxis: { title: { text: `${t('maturityTarget.datumQ.strength', 'Strength')} (${result.strength_unit})` }, min: 0 },
      tooltip: { pointFormat: `Age: <b>{point.x}</b> d<br/>Strength: <b>{point.y}</b> ${result.strength_unit}` },
      series,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, analyzed, t]);

  const datumChart = useMemo<Highcharts.Options | null>(() => {
    if (!result || !result.datum_fit) return null;
    const temps = result.baths.map((b) => b.bath_temp_c);
    const to = result.datum_temp_c;
    const xMin = Math.min(to ?? temps[0], temps[0]) - 5;
    const xMax = Math.max(...temps) + 5;
    const { slope, intercept } = result.datum_fit;
    const series: Highcharts.SeriesOptionsType[] = [
      {
        type: 'scatter',
        name: t('maturityTarget.datumQ.kValues', 'k values'),
        data: result.baths.map((b) => [b.bath_temp_c, Number(b.k.toFixed(4))]),
        color: '#1976d2',
        marker: { radius: 6, symbol: 'circle' },
        zIndex: 3,
      },
      {
        type: 'line',
        name: t('maturityTarget.datumQ.bestFitLine', 'Best-fit line'),
        data: linePoints(slope, intercept, to ?? xMin, xMax),
        color: '#555',
        dashStyle: 'ShortDash',
        marker: { enabled: false },
        enableMouseTracking: false,
        zIndex: 2,
      },
    ];
    if (to !== null) {
      series.push({
        type: 'scatter',
        name: `${t('maturityTarget.datumQ.datumTemp', 'Datum temperature')} To = ${fmt(to, 1)} °C`,
        data: [[Number(to.toFixed(2)), 0]],
        color: '#c62828',
        marker: { radius: 8, symbol: 'diamond' },
        dataLabels: { enabled: true, format: `To = ${fmt(to, 1)} °C`, y: -10 },
        zIndex: 4,
      });
    }
    return {
      ...baseChart,
      chart: { height: 480 },
      title: { text: t('maturityTarget.datumQ.chartDatumTitle', 'Step 2: k vs bath temperature (datum temperature)') },
      subtitle: { text: t('maturityTarget.datumQ.chartDatumSub', 'Where the line crosses k = 0 is the datum temperature for Nurse-Saul.') },
      xAxis: {
        title: { text: t('maturityTarget.datumQ.bathTempC', 'Bath temperature (°C)') },
        min: xMin,
        max: xMax,
        plotLines: to !== null ? [{ value: to, color: '#c62828', width: 1, dashStyle: 'Dot' }] : [],
      },
      yAxis: { title: { text: 'k (1/day)' }, min: 0, plotLines: [{ value: 0, color: '#999', width: 1 }] },
      tooltip: { pointFormat: 'T: <b>{point.x}</b> °C<br/>k: <b>{point.y}</b> 1/day' },
      series,
    };
  }, [result, t]);

  const qChart = useMemo<Highcharts.Options | null>(() => {
    if (!result || !result.q_fit) return null;
    const xs = result.baths.map((b) => b.inv_temp_k);
    const pad = (Math.max(...xs) - Math.min(...xs)) * 0.15 || 0.0001;
    const x0 = Math.min(...xs) - pad;
    const x1 = Math.max(...xs) + pad;
    const { slope, intercept } = result.q_fit;
    return {
      ...baseChart,
      chart: { height: 480 },
      title: { text: t('maturityTarget.datumQ.chartQTitle', 'Step 3: ln(k) vs 1/T (Q-value)') },
      subtitle: {
        text: t('maturityTarget.datumQ.chartQSub', 'The Q-value is the negative slope of this line. T is in kelvin.'),
      },
      xAxis: {
        title: { text: '1 / T (1/K)' },
        min: x0,
        max: x1,
        labels: { format: '{value:.5f}' },
      },
      yAxis: { title: { text: 'ln k' } },
      tooltip: { pointFormat: '1/T: <b>{point.x:.6f}</b> 1/K<br/>ln k: <b>{point.y:.3f}</b>' },
      series: [
        {
          type: 'scatter',
          name: 'ln k',
          data: result.baths.map((b) => [b.inv_temp_k, Number(b.ln_k.toFixed(4))]),
          color: '#e65100',
          marker: { radius: 6, symbol: 'circle' },
          zIndex: 3,
        },
        {
          type: 'line',
          name: `${t('maturityTarget.datumQ.bestFitLine', 'Best-fit line')}  (Q = ${fmt(result.q_value_k, 0)} K)`,
          data: linePoints(slope, intercept, x0, x1),
          color: '#555',
          dashStyle: 'ShortDash',
          marker: { enabled: false },
          enableMouseTracking: false,
          zIndex: 2,
        },
      ],
    };
  }, [result, t]);

  // ---- render -------------------------------------------------------------
  const errorWarnings = result?.warnings.filter((w) => w.severity === 'error') ?? [];
  const otherWarnings = result?.warnings.filter((w) => w.severity !== 'error') ?? [];

  return (
    <Box>
      {/* Intro + help */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <ScienceIcon color="primary" />
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('maturityTarget.datumQ.title', 'Determine datum temperature and Q-value from lab data')}
          </Typography>
          <Chip size="small" icon={<MenuBookIcon />} label="ASTM C1074-19, Appendix X1" variant="outlined" />
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t(
            'maturityTarget.datumQ.intro',
            'The Nurse-Saul method needs a datum temperature. The Arrhenius method needs a Q-value (activation energy). Default values are only approximate. This tool calculates the correct values for your concrete mix from mortar cube tests at three or more curing temperatures.',
          )}
        </Typography>
        <Accordion expanded={helpOpen} onChange={() => setHelpOpen((o) => !o)} disableGutters sx={{ mt: 1, boxShadow: 'none', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography variant="subtitle2">{t('maturityTarget.datumQ.howTitle', 'How this works')}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>{t('maturityTarget.datumQ.labTitle', 'In the laboratory')}</Typography>
                <Typography variant="body2" component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
                  <li>{t('maturityTarget.datumQ.lab1', 'Make a mortar with the same cement, water-cement ratio and admixtures as the concrete. Use a sand-to-cement ratio equal to the coarse-aggregate-to-cement ratio of the concrete.')}</li>
                  <li>{t('maturityTarget.datumQ.lab2', 'Cast at least 21 mortar cubes (50 mm) for each of three water baths. Set one bath at the lowest and one at the highest temperature the concrete will see on site. Set the third bath near the middle.')}</li>
                  <li>{t('maturityTarget.datumQ.lab3', 'Record the time when water first touches the cement. This is age zero.')}</li>
                  <li>{t('maturityTarget.datumQ.lab4', 'Break three cubes at the first age when the strength is about 4 MPa. Then break three cubes at about double the previous age, for example 1, 2, 4, 8, 16 and 32 days.')}</li>
                  <li>{t('maturityTarget.datumQ.lab5', 'For each age, enter the average age of the three cubes (to 0.01 day) and their average strength.')}</li>
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle2" gutterBottom>{t('maturityTarget.datumQ.calcTitle', 'What the calculation does')}</Typography>
                <Typography variant="body2" component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
                  <li>{t('maturityTarget.datumQ.calc1', 'For each bath, it fits a curve to strength against age (Eq X1.1). The curve gives the rate constant k. A larger k means the mortar gains strength faster.')}</li>
                  <li>{t('maturityTarget.datumQ.calc2', 'Datum temperature: it draws the best straight line through k against bath temperature. The temperature where k would be zero is the datum temperature. Below this temperature the concrete gains no strength.')}</li>
                  <li>{t('maturityTarget.datumQ.calc3', 'Q-value: it draws the best straight line through ln(k) against 1/T, with T in kelvin. The negative slope is the Q-value. Activation energy = Q × 8.31 J/mol.')}</li>
                  <li>{t('maturityTarget.datumQ.calc4', 'Save the results as a Nurse-Saul group or an Arrhenius group. Use the same values for the strength-maturity calibration and for the pour. ASTM 9.2 requires this.')}</li>
                </Typography>
                <Alert severity="info" sx={{ mt: 1.5 }} icon={false}>
                  <Typography variant="body2">
                    {t('maturityTarget.datumQ.checkHint', 'To check the tool, load the ASTM worked example. The standard reports a datum temperature of 3.4 °C and a Q-value of 4900 K for that data.')}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Paper>

      {/* Toolbar */}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">{t('maturityTarget.datumQ.strengthUnit', 'Strength unit')}:</Typography>
        <ToggleButtonGroup size="small" exclusive value={unit} onChange={(_e, v) => v && setUnit(v)}>
          {UNITS.map((u) => <ToggleButton key={u} value={u} sx={{ textTransform: 'none', px: 1.5 }}>{u}</ToggleButton>)}
        </ToggleButtonGroup>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="outlined" startIcon={<MenuBookIcon />} onClick={loadExample} sx={{ textTransform: 'none' }}>
          {t('maturityTarget.datumQ.loadExample', 'Load ASTM worked example')}
        </Button>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addBath} sx={{ textTransform: 'none' }}>
          {t('maturityTarget.datumQ.addBath', 'Add bath')}
        </Button>
        <Button size="small" variant="text" startIcon={<ResetIcon />} onClick={reset} sx={{ textTransform: 'none' }}>
          {t('maturityTarget.datumQ.clear', 'Clear')}
        </Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
          onClick={calculate}
          disabled={loading}
          sx={{ textTransform: 'none' }}
        >
          {t('maturityTarget.datumQ.calculate', 'Calculate')}
        </Button>
      </Stack>

      {isExampleLoaded && example && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('maturityTarget.datumQ.exampleLoaded', {
            defaultValue: 'Loaded {{source}}. The standard reports To = {{to}} °C and Q = {{q}} K for this data.',
            source: example.source,
            to: example.expected_datum_temp_c,
            q: example.expected_q_value_k,
          })}
        </Alert>
      )}

      {/* Data entry grid */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {baths.map((bath, i) => (
          <Grid key={bath.id} size={{ xs: 12, md: 6, lg: 4 }}>
            <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length], flexShrink: 0 }} />
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  {t('maturityTarget.datumQ.bathN', { defaultValue: 'Bath {{n}}', n: i + 1 })}
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  label={t('maturityTarget.datumQ.bathTemp', 'Bath temperature')}
                  value={bath.tempC}
                  onChange={(e) => updateBath(bath.id, { tempC: e.target.value })}
                  InputProps={{ endAdornment: <InputAdornment position="end">°C</InputAdornment> }}
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={{ width: 150 }}
                />
                <Tooltip title={baths.length <= MIN_BATHS ? t('maturityTarget.datumQ.minBaths', 'Three baths are the minimum') : t('maturityTarget.datumQ.removeBath', 'Remove bath')}>
                  <span>
                    <IconButton size="small" onClick={() => removeBath(bath.id)} disabled={baths.length <= MIN_BATHS} aria-label="remove bath">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
              <Table size="small" sx={{ '& td, & th': { px: 0.5, py: 0.25, border: 0 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 28 }}>#</TableCell>
                    <TableCell>{t('maturityTarget.datumQ.ageDays', 'Age (days)')}</TableCell>
                    <TableCell>{labelWithUnit(t('maturityTarget.datumQ.strength', 'Strength'), unit)}</TableCell>
                    <TableCell sx={{ width: 36 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bath.rows.map((row, r) => (
                    <TableRow key={row.id}>
                      <TableCell><Typography variant="caption" color="text.secondary">{r + 1}</Typography></TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.age}
                          onChange={(e) => updateRow(bath.id, row.id, 'age', e.target.value)}
                          inputProps={{ step: 'any', min: 0, 'aria-label': `bath ${i + 1} age ${r + 1}` }}
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          fullWidth
                          value={row.strength}
                          onChange={(e) => updateRow(bath.id, row.id, 'strength', e.target.value)}
                          inputProps={{ step: 'any', min: 0, 'aria-label': `bath ${i + 1} strength ${r + 1}` }}
                          variant="standard"
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => removeRow(bath.id, row.id)} aria-label="remove row">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addRow(bath.id)} sx={{ mt: 0.5, textTransform: 'none' }}>
                {t('maturityTarget.datumQ.addAge', 'Add test age')}
              </Button>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {formErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>{t('maturityTarget.datumQ.fixBeforeCalc', 'Please complete the data before calculating')}</AlertTitle>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {formErrors.map((e) => <li key={e}>{e}</li>)}
          </Box>
        </Alert>
      )}
      {apiError && <Alert severity="error" sx={{ mb: 2 }}>{apiError}</Alert>}

      {/* Results */}
      {result && (
        <Box data-testid="datum-q-results">
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: 'primary.light' }}>
                <Typography variant="overline" color="text.secondary">{t('maturityTarget.nurseSaulTab', 'Nurse-Saul')}</Typography>
                <Typography variant="subtitle1">{t('maturityTarget.datumQ.datumTemp', 'Datum temperature')} To</Typography>
                {result.datum_temp_c !== null ? (
                  <>
                    <Typography variant="h4" sx={{ my: 0.5 }}>
                      {fmt(result.datum_temp_c, 1)} °C
                      <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                        ({fmt(cToFNumber(result.datum_temp_c), 1)} °F)
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      k = {fmt(result.datum_fit?.slope ?? null, 5)} × T {(result.datum_fit?.intercept ?? 0) < 0 ? '−' : '+'} {fmt(Math.abs(result.datum_fit?.intercept ?? 0), 4)}
                      {'   ·   '}R² = {fmt(result.datum_fit?.r2 ?? null, 3)}
                    </Typography>
                    {isExampleLoaded && example && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        {t('maturityTarget.datumQ.standardReports', { defaultValue: 'Standard reports {{v}}', v: `${example.expected_datum_temp_c} °C` })}
                      </Typography>
                    )}
                    {canManage && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        sx={{ mt: 1.5, textTransform: 'none' }}
                        onClick={() =>
                          onUseForNurseSaul({
                            datum_temp: fmt(result.datum_temp_c, 1),
                            group_description: t('maturityTarget.datumQ.descDatum', 'Datum temp from ASTM C1074 X1 lab test'),
                          })
                        }
                      >
                        {t('maturityTarget.datumQ.useNurseSaul', 'Save as Nurse-Saul group')}
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="error">{t('maturityTarget.datumQ.notDetermined', 'Could not be determined from this data.')}</Typography>
                )}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%', borderColor: 'warning.light' }}>
                <Typography variant="overline" color="text.secondary">{t('maturityTarget.arrheniusTab', 'Arrhenius')}</Typography>
                <Typography variant="subtitle1">{t('maturityTarget.datumQ.qValue', 'Q-value')}</Typography>
                {result.q_value_k !== null ? (
                  <>
                    <Typography variant="h4" sx={{ my: 0.5 }}>
                      {fmt(result.q_value_k, 0)} K
                      <Typography component="span" variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                        (E = {result.activation_energy_j_mol?.toLocaleString()} J/mol)
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ln k = {fmt(result.q_fit?.intercept ?? null, 3)} − {fmt(result.q_value_k, 0)} × (1/T)
                      {'   ·   '}R² = {fmt(result.q_fit?.r2 ?? null, 3)}
                    </Typography>
                    {isExampleLoaded && example && (
                      <Typography variant="body2" color="success.main" sx={{ mt: 0.5 }}>
                        {t('maturityTarget.datumQ.standardReports', { defaultValue: 'Standard reports {{v}}', v: `${example.expected_q_value_k} K` })}
                      </Typography>
                    )}
                    {canManage && (
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        startIcon={<SaveIcon />}
                        sx={{ mt: 1.5, textTransform: 'none' }}
                        onClick={() =>
                          onUseForArrhenius({
                            activation_energy: String(result.activation_energy_j_mol ?? ''),
                            specified_temp: DEFAULT_SPECIFIED_TEMP_C,
                            group_description: t('maturityTarget.datumQ.descQ', 'Q-value from ASTM C1074 X1 lab test'),
                          })
                        }
                      >
                        {t('maturityTarget.datumQ.useArrhenius', 'Save as Arrhenius group')}
                      </Button>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="error">{t('maturityTarget.datumQ.notDetermined', 'Could not be determined from this data.')}</Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          {errorWarnings.map((w) => <Alert key={w.code + (w.bath_temp_c ?? '')} severity="error" sx={{ mb: 1 }}>{w.message}</Alert>)}
          {otherWarnings.map((w) => (
            <Alert key={w.code + (w.bath_temp_c ?? '')} severity={w.severity === 'warning' ? 'warning' : 'info'} sx={{ mb: 1 }}>
              {w.message}
            </Alert>
          ))}

          {/* Datum and Q charts: the answers, so they sit first and larger */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, lg: 6 }}>
              {datumChart && <Paper variant="outlined" sx={{ p: 1 }}><HighchartsReact highcharts={Highcharts} options={datumChart} /></Paper>}
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              {qChart && <Paper variant="outlined" sx={{ p: 1 }}><HighchartsReact highcharts={Highcharts} options={qChart} /></Paper>}
            </Grid>
          </Grid>

          {/* Per-bath fit table */}
          <Paper variant="outlined" sx={{ p: 1.5, my: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('maturityTarget.datumQ.fitTableTitle', 'Curve fit for each bath (Eq X1.1)')}
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('maturityTarget.datumQ.bathTempC', 'Bath temperature (°C)')}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('maturityTarget.datumQ.suHelp', 'Limiting strength the curve levels off at')}><span>Su ({result.strength_unit})</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('maturityTarget.datumQ.kHelp', 'Rate constant. Larger k = faster strength gain')}><span>k (1/day)</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('maturityTarget.datumQ.t0Help', 'Age when strength gain starts')}><span>t0 (day)</span></Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('maturityTarget.datumQ.rsdHelp', 'Residual standard deviation of the fit. Smaller is better')}><span>RSD ({result.strength_unit})</span></Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.baths.map((b, i) => (
                  <TableRow key={b.bath_temp_c}>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorForTemp(b.bath_temp_c, i) }} />
                        <span>{b.bath_temp_c}</span>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">{fmt(b.su, 1)}</TableCell>
                    <TableCell align="right"><b>{fmt(b.k, 3)}</b></TableCell>
                    <TableCell align="right">{fmt(b.t0, 2)}</TableCell>
                    <TableCell align="right">{fmt(b.rsd, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* Strength vs age chart */}
          {strengthChart && <Paper variant="outlined" sx={{ p: 1 }}><HighchartsReact highcharts={Highcharts} options={strengthChart} /></Paper>}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('maturityTarget.datumQ.footnote', {
              defaultValue: 'Kelvin = °C + {{k}}. Activation energy = Q × {{r}} J/mol. These are the same constants ConXedge uses when it calculates maturity on a pour.',
              k: result.kelvin_offset,
              r: result.gas_constant,
            })}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default DatumQWorkbench;
